import * as React from "react";
import { PropTypes } from "prop-types";
import { InView } from "react-intersection-observer";
import { ColorSwatch } from "@lululemon/ecom-pattern-library-color-swatch";
import { Icon } from "@lululemon/ecom-pattern-library-icon";
import { plusIcon } from "@lululemon/ecom-pattern-library-icons";
import Link from "next/link";
import DesktopSwatchCarousel from "./desktop-swatch-carousel";
import { useLaunchDarklyFlag } from "@lululemon/mwa-launch-darkly";
import { publicConfig } from "@lululemon/ecom-common/src/util/public-config";

import { capitalize } from "../../util/helpers";

import { SignalContext } from "../../services/context-service";

import {
  analyticsProductLinkDataAttributes,
  formatRvRecosTracking,
} from "../../lib/analytics";

import "./slider.scss";
import "./swatch-carousel.scss";
import { handleOnSwatch } from "@lululemon/ecom-wishlist/src/utils/helpers";

function WrappedLink({ route, isMobileDevice, ...rest }) {
  return (
    <>
      {isMobileDevice ? (
        <a {...rest} />
      ) : (
        <Link href={route}>
          <a {...rest} />
        </Link>
      )}
    </>
  );
}

const SwatchCarousel = ({
  analyticsAttrs,
  disabledColorIds,
  pdpLink,
  selectedColor,
  sizeUrlQueryString,
  swatchList,
  getSwatchIndex,
  product,
  activeSwatch,
  productIndex,
  bp,
  showBopis,
  dataType,
  cdpSerpSwatch,
  wishlists,
  setIsItemInWishlist,
}) => {
  const onSwatchHandler = React.useCallback(
    (url) => {
      handleOnSwatch({
        url,
        productId: product?.["product-id"],
        wishlists,
        setIsItemInWishlist,
      });
    },
    [wishlists]
  );

  const largeColourSwatchLDFlag = useLaunchDarklyFlag(
    "cdp-serp-large-colour-swatch",
    false,
    publicConfig?.launchDarkly?.clientSideId
  );

  const isMobileDevice = ["small", "mobile", "tablet"].includes(bp);
  const isLargeColourSwatch = largeColourSwatchLDFlag && isMobileDevice;
  const trimmedSwatchList =
    !largeColourSwatchLDFlag && isMobileDevice
      ? swatchList.slice(0, 5)
      : swatchList;

  const queryString = React.useMemo(() => {
    if (typeof sizeUrlQueryString === "function") {
      return sizeUrlQueryString(selectedColor);
    }
    return sizeUrlQueryString || "";
  }, [sizeUrlQueryString]);

  const analyticsOverrides = React.useMemo(() => {
    const { skuId, styleId, priceProd } = formatRvRecosTracking(product);
    const analyticsOverrideConfig = {
      "you may also like": { dataLuluTrack: "pdp-ymal-recommendation" },
      "recently viewed": {
        component: "carousel",
        dataLuluTrack: "recently-viewed-recommendations-product",
        version: 2.3,
        size: "",
        quantity: "",
        skuID: skuId,
        styleID: styleId,
        price: priceProd,
      },
    };
    return analyticsOverrideConfig[dataType] || {};
  }, [dataType, product]);

  const signalContext = React.useContext(SignalContext);

  const handleSendFilterSignals = React.useCallback(
    (selectedColorId, colorList) => () => {
      let colourGroup = colorList.filter(
        (color) => color["color-id"] === selectedColorId
      )[0]["color-group"];
      colourGroup = Array.isArray(colourGroup)
        ? colourGroup[0].split("|")[0]
        : colourGroup;
      const fields = {
        type: "filter",
        filter_name: "colour",
        filter_value: colourGroup,
        fusion_query_id: signalContext?.fusionQueryId,
      };
    },
    [signalContext]
  );

  const swatchArr = trimmedSwatchList.map((value, index) => {
    const defaultSku = (product["sku-style-order"] || []).find(
      (color) => color["color-id"] === value.id
    )?.sku;
    const analyticsSwatchIndex = index + 1;
    const colorID = value.id;
    const swatchAnalyticsAttrs = analyticsProductLinkDataAttributes({
      product,
      isBOPIS: showBopis,
      productIndex,
      swatchIndex: analyticsSwatchIndex,
      colorID,
      overrides: analyticsOverrides,
    });
    return (
      <li key={`swatch-arr--${value.url}`}>
        <WrappedLink
          className="swatch-carousel__link"
          onMouseEnter={(event) => {
            getSwatchIndex(event, value.url, index);
            onSwatchHandler(value.url);
          }}
          onTouchStart={(event) => {
            event.preventDefault();
            getSwatchIndex(event, value.url, index);
            onSwatchHandler(value.url);
          }}
          route={`${pdpLink}?color=${selectedColor}${queryString}`}
          onFocus={(event) => {
            if (isLargeColourSwatch) {
              return;
            }
            getSwatchIndex(event, value.url, index);
            onSwatchHandler(value.url);
          }}
          onKeyDown={(event) => {
            if (isLargeColourSwatch && event.key === "Enter") {
              getSwatchIndex(event, value.url, index);
              onSwatchHandler(value.url);
            }
          }}
          onClick={handleSendFilterSignals(
            selectedColor,
            product["sku-style-order"]
          )}
          {...swatchAnalyticsAttrs}
          data-skuid={defaultSku} // override value in analyticsAttrs
          isMobileDevice={isMobileDevice}
          rel={cdpSerpSwatch ? "nofollow" : undefined}
          href={isLargeColourSwatch ? "javascript:void(0)" : undefined}
        >
          <ColorSwatch
            data-testid={
              activeSwatch === index ? "color-swatch--active" : "color-swatch"
            }
            id={value.id}
            name={capitalize(value.name)}
            isSelected={activeSwatch === index}
            isDisabled={disabledColorIds.has(value.id)}
            className={
              isLargeColourSwatch
                ? "large-swatch-carousel__item"
                : "swatch-carousel__item"
            }
          />
        </WrappedLink>
      </li>
    );
  });

  return (
    <InView triggerOnce rootMargin="200px">
      {({ inView, ref }) => (
        <div ref={ref} className="swatch-carousel">
          {isMobileDevice && inView && (
            <ul
              className={
                isLargeColourSwatch
                  ? "large-swatch-carousel-mobile"
                  : "swatch-carousel-mobile"
              }
            >
              {swatchArr}
              {!largeColourSwatchLDFlag && swatchList.length > 5 && (
                <li>
                  <WrappedLink
                    className="swatch-carousel-mobile__more-icon-link"
                    route={pdpLink}
                    {...analyticsAttrs}
                  >
                    <Icon
                      className="swatch-carousel-mobile__more-icon"
                      content={plusIcon}
                      title="all colors"
                    />
                  </WrappedLink>
                </li>
              )}
            </ul>
          )}
          {!isMobileDevice && (
            <DesktopSwatchCarousel {...{ activeSwatch, inView }}>
              {swatchArr}
            </DesktopSwatchCarousel>
          )}
        </div>
      )}
    </InView>
  );
};

SwatchCarousel.defaultProps = {
  analyticsAttrs: {},
  activeSwatch: undefined,
  disabledColorIds: new Set(),
  pdpLink: "",
  product: [],
  selectedColor: undefined,
  sizeUrlQueryString: undefined,
  productIndex: undefined,
  swatchList: [],
  bp: "desktop",
  showBopis: false,
  dataType: "",
  cdpSerpSwatch: false,
};

SwatchCarousel.propTypes = {
  analyticsAttrs: PropTypes.shape({
    "data-component-link": PropTypes.string,
    "data-productid": PropTypes.string,
    "data-categoryunifiedid": PropTypes.string,
  }),
  disabledColorIds: PropTypes.instanceOf(Set),
  swatchList: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
      id: PropTypes.string,
    })
  ),
  pdpLink: PropTypes.string,
  activeSwatch: PropTypes.number,
  getSwatchIndex: PropTypes.func.isRequired,
  selectedColor: PropTypes.string,
  sizeUrlQueryString: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  product: PropTypes.shape({
    "sku-style-order": PropTypes.array,
  }),
  bp: PropTypes.string,
  showBopis: PropTypes.bool,
  productIndex: PropTypes.number,
  dataType: PropTypes.string,
  cdpSerpSwatch: PropTypes.bool,
  wishlists: PropTypes.array,
  getSwatchIndex: PropTypes.func,
};

export default SwatchCarousel;
